'use client';

import { css } from '@/styled-system/css';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { CostAndFeesSpokeContent as VercelCostAndFeesSpokeContent } from '@/src/components/features/property/CostAndFeesModule/spokes/CostAndFeesSpokeContent';
import type { CostAndFeesCalculatorState } from '@/src/components/features/property/CostAndFeesModule/useCostAndFeesCalculatorState';
import { useDetailPanelSafe } from '@/src/components/features/property/ui/DetailPanel';

/**
 * HALO CostAndFeesSpokeContent adapter.
 *
 * The legacy HALO panel wired `CostsAndFeesContextProvider` + calculators. The
 * vercel `features/property/CostAndFeesModule/spokes/CostAndFeesSpokeContent`
 * already renders a complete breakdown (EstimateCostSection, UnitPicker,
 * LeaseTermsInline) using the embedded costs data. HALO wraps that so the
 * accordion module can reuse the same detailed body.
 */

const wrapper = css({
  width: '100%',
  height: '100%',
  overflow: 'auto',
});

type SpokeContentProps = {
  config?: unknown;
  listing?: PropertyDetailListing;
  showHeader?: boolean;
  bodyClassName?: string;
  collapseFeeBreakdown?: boolean;
  showEstimateSpecialOffer?: boolean;
  showEstimateSummary?: boolean;
  calculatorState?: CostAndFeesCalculatorState;
};

export const CostAndFeesSpokeContent = ({
  config,
  listing,
  showHeader,
  bodyClassName,
  collapseFeeBreakdown,
  showEstimateSpecialOffer,
  showEstimateSummary,
  calculatorState,
}: SpokeContentProps) => {
  const panel = useDetailPanelSafe();
  const effectiveListing =
    (listing as PropertyDetailListing | undefined) ??
    (config as PropertyDetailListing | undefined) ??
    (panel?.panelConfig as PropertyDetailListing | undefined);

  if (!effectiveListing) return null;

  return (
    <div className={wrapper}>
      <VercelCostAndFeesSpokeContent
        panelProps={{ listing: effectiveListing }}
        showHeader={showHeader}
        bodyClassName={bodyClassName}
        collapseFeeBreakdown={collapseFeeBreakdown}
        showEstimateSpecialOffer={showEstimateSpecialOffer}
        showEstimateSummary={showEstimateSummary}
        calculatorState={calculatorState}
      />
    </div>
  );
};
